/* PinchZoom — the poster's gesture surface (extracted from ProjectScreen for
   DESIGN47 so the full-screen viewer reuses the exact same maths). */

import React, {
  forwardRef, useCallback, useEffect, useImperativeHandle, useRef,
} from 'react';
import { haptic } from '../telegram';

export interface PinchZoomHandle {
  /** Multiply the current scale (about the stage centre), clamped to [fit, 6×fit]. */
  zoomBy(factor: number): void;
  /** Back to the initial view. */
  fit(): void;
}

/* ─────────────────────── pinch-zoom / pan ───────────────────────
   A self-contained gesture surface: the poster fits to width on load, then
   two-finger pinch zooms around the focal point, one-finger drag pans any
   overflowing content, and a double-tap toggles between fit and a 2.5× view centred on the
   tap. Mouse fallbacks (wheel + drag + dbl-click) keep it usable in a dev
   browser. All transforms are written straight to the node (no per-frame
   React state) so gestures stay buttery. */

interface ZoomState {
  scale: number;
  min: number;
  tx: number;
  ty: number;
  natW: number;
  natH: number;
  ww: number;
  wh: number;
}

export const PinchZoom = forwardRef<PinchZoomHandle, {
  src: string;
  resetKey: number;
  /** Fit to the stage's HEIGHT as well as its width (the full-screen viewer);
      the tab keeps fit-to-width. */
  contain?: boolean;
  /** A plain tap (no drag, no pinch) on the poster. */
  onTap?: (point?: { x: number; y: number }) => void;
  /** Open a pinned comment at its normalized point on the poster. */
  targetPoint?: { x: number; y: number } | null;
  className?: string;
}>(function PinchZoom({ src, resetKey, contain, onTap, targetPoint, className }, ref) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const markerRef = useRef<HTMLSpanElement>(null);
  const st = useRef<ZoomState>({
    scale: 1,
    min: 1,
    tx: 0,
    ty: 0,
    natW: 1,
    natH: 1,
    ww: 1,
    wh: 1,
  });
  const gesture = useRef<{
    mode: 'none' | 'pan' | 'pinch';
    startDist: number;
    scale0: number;
    tx0: number;
    ty0: number;
    fx: number;
    fy: number;
    px: number;
    py: number;
  }>({
    mode: 'none',
    startDist: 0,
    scale0: 1,
    tx0: 0,
    ty0: 0,
    fx: 0,
    fy: 0,
    px: 0,
    py: 0,
  });
  const lastTap = useRef(0);
  // A tap = one finger down and up within 8px and 400ms, no pinch.
  const tapStart = useRef<{ x: number; y: number; t: number; moved: boolean } | null>(null);
  const tapTimer = useRef<number | undefined>(undefined);
  useEffect(() => () => clearTimeout(tapTimer.current), []);

  const apply = useCallback(() => {
    const el = imgRef.current;
    if (!el) return;
    const { scale, tx, ty } = st.current;
    el.style.transform = `translate3d(${tx}px, ${ty}px, 0) scale(${scale})`;
    if (markerRef.current && targetPoint) {
      markerRef.current.style.left = `${tx + targetPoint.x * st.current.natW * scale}px`;
      markerRef.current.style.top = `${ty + targetPoint.y * st.current.natH * scale}px`;
    }
  }, [targetPoint]);

  const clamp = useCallback(() => {
    const s = st.current;
    const cw = s.natW * s.scale;
    const ch = s.natH * s.scale;
    if (cw <= s.ww) s.tx = (s.ww - cw) / 2;
    else s.tx = Math.min(0, Math.max(s.ww - cw, s.tx));
    if (ch <= s.wh) s.ty = (s.wh - ch) / 2;
    else s.ty = Math.min(0, Math.max(s.wh - ch, s.ty));
  }, []);

  const fit = useCallback(() => {
    const wrap = wrapRef.current;
    const img = imgRef.current;
    if (!wrap || !img) return;
    const natW = img.naturalWidth || 1;
    const natH = img.naturalHeight || 1;
    const ww = wrap.clientWidth || 1;
    const wh = wrap.clientHeight || 1;
    const s = contain ? Math.min(ww / natW, wh / natH) : ww / natW;
    st.current = { scale: s, min: s, tx: 0, ty: 0, natW, natH, ww, wh };
    clamp();
    apply();
  }, [apply, clamp, contain]);

  const focusTarget = (point: { x: number; y: number }) => {
    const s = st.current;
    if (!s.natW || !s.natH || !s.ww || !s.wh) return;
    s.scale = s.min * 2.5;
    s.tx = s.ww / 2 - point.x * s.natW * s.scale;
    s.ty = s.wh / 2 - point.y * s.natH * s.scale;
    clamp();
    apply();
  };

  // Re-fit when the poster changes or the caller bumps resetKey.
  useEffect(() => {
    fit();
  }, [resetKey, src, fit]);

  useEffect(() => {
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => {
      const wrap = wrapRef.current;
      // The initial observation often arrives after a cached image's load.
      // Leave its focused comment location and the user's zoom intact.
      if (wrap && (wrap.clientWidth !== st.current.ww || wrap.clientHeight !== st.current.wh)) fit();
    }) : null;
    if (wrapRef.current) observer?.observe(wrapRef.current);
    window.addEventListener('resize', fit);
    return () => { observer?.disconnect(); window.removeEventListener('resize', fit); };
  }, [fit]);

  const zoomBy = useCallback((factor: number) => {
    const s = st.current;
    const x = s.ww / 2;
    const y = s.wh / 2;
    const ns = Math.min(s.min * 6, Math.max(s.min, s.scale * factor));
    const f = ns / s.scale;
    s.tx = x - (x - s.tx) * f;
    s.ty = y - (y - s.ty) * f;
    s.scale = ns;
    clamp();
    apply();
  }, [apply, clamp]);
  useImperativeHandle(ref, () => ({ zoomBy, fit }), [zoomBy, fit]);

  const rectPoint = (clientX: number, clientY: number) => {
    const r = wrapRef.current?.getBoundingClientRect();
    return { x: clientX - (r?.left || 0), y: clientY - (r?.top || 0) };
  };
  const imagePoint = (clientX: number, clientY: number) => {
    const rect = imgRef.current?.getBoundingClientRect();
    if (!rect?.width || !rect.height) return undefined;
    const x = (clientX - rect.left) / rect.width;
    const y = (clientY - rect.top) / rect.height;
    return x >= 0 && x <= 1 && y >= 0 && y <= 1 ? { x, y } : undefined;
  };

  const doubleTap = (clientX: number, clientY: number) => {
    const s = st.current;
    const { x, y } = rectPoint(clientX, clientY);
    if (s.scale > s.min * 1.05) {
      fit();
    } else {
      const ns = s.min * 2.5;
      const f = ns / s.scale;
      s.tx = x - (x - s.tx) * f;
      s.ty = y - (y - s.ty) * f;
      s.scale = ns;
      clamp();
      apply();
    }
    haptic.select();
  };

  const onTouchStart = (e: React.TouchEvent) => {
    const g = gesture.current;
    if (e.touches.length === 2) {
      const [a, b] = [e.touches[0], e.touches[1]];
      const mid = rectPoint(
        (a.clientX + b.clientX) / 2,
        (a.clientY + b.clientY) / 2,
      );
      g.mode = 'pinch';
      lastTap.current = 0;
      g.startDist = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY) || 1;
      g.scale0 = st.current.scale;
      g.tx0 = st.current.tx;
      g.ty0 = st.current.ty;
      g.fx = mid.x;
      g.fy = mid.y;
      tapStart.current = null;
      clearTimeout(tapTimer.current);
    } else if (e.touches.length === 1) {
      const t = e.touches[0];
      g.mode = 'pan';
      g.px = t.clientX;
      g.py = t.clientY;
      tapStart.current = { x: t.clientX, y: t.clientY, t: Date.now(), moved: false };
    }
  };

  const onTouchMove = (e: React.TouchEvent) => {
    const g = gesture.current;
    const s = st.current;
    if (g.mode === 'pinch' && e.touches.length >= 2) {
      e.preventDefault();
      const [a, b] = [e.touches[0], e.touches[1]];
      const dist = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY) || 1;
      const ns = Math.min(
        s.min * 6,
        Math.max(s.min, g.scale0 * (dist / g.startDist)),
      );
      const f = ns / g.scale0;
      const mid = rectPoint((a.clientX + b.clientX) / 2, (a.clientY + b.clientY) / 2);
      s.scale = ns;
      s.tx = mid.x - (g.fx - g.tx0) * f;
      s.ty = mid.y - (g.fy - g.ty0) * f;
      clamp();
      apply();
    } else if (g.mode === 'pan' && e.touches.length === 1) {
      const t = e.touches[0];
      const dx = t.clientX - g.px;
      const dy = t.clientY - g.py;
      const ts = tapStart.current;
      if (ts && Math.hypot(t.clientX - ts.x, t.clientY - ts.y) > 8) ts.moved = true;
      // Long architectures need vertical panning even in their initial
      // fit-to-width view. No page scroll may escape this gesture surface.
      if (s.natW * s.scale > s.ww || s.natH * s.scale > s.wh) {
        e.preventDefault();
        s.tx += dx;
        s.ty += dy;
        g.px = t.clientX;
        g.py = t.clientY;
        clamp();
        apply();
      }
    }
  };

  const onTouchEnd = (e: React.TouchEvent) => {
    const g = gesture.current;
    lastTouchAt.current = Date.now();
    if (e.touches.length === 0 && g.mode === 'pan') {
      const now = Date.now();
      const t = e.changedTouches[0];
      const ts = tapStart.current;
      tapStart.current = null;
      const isTap = ts && !ts.moved && now - ts.t < 400;
      if (isTap && now - lastTap.current < 300) {
        lastTap.current = 0;
        clearTimeout(tapTimer.current);
        if (t) doubleTap(t.clientX, t.clientY);
      } else if (isTap) {
        lastTap.current = now;
        // A single tap fires only if no second tap follows (double-tap = zoom).
        if (onTap) {
          clearTimeout(tapTimer.current);
          tapTimer.current = window.setTimeout(() => {
            lastTap.current = 0;
            onTap(imagePoint(t.clientX, t.clientY));
          }, 280);
        }
      }
    }
    if (e.touches.length === 0) g.mode = 'none';
    else if (e.touches.length === 1) {
      g.mode = 'pan';
      g.px = e.touches[0].clientX;
      g.py = e.touches[0].clientY;
    }
  };

  // Mouse fallbacks for the dev browser.
  const dragging = useRef(false);
  const onMouseDown = (e: React.MouseEvent) => {
    dragging.current = true;
    mouseMoved.current = false;
    gesture.current.px = e.clientX;
    gesture.current.py = e.clientY;
  };
  const onMouseMove = (e: React.MouseEvent) => {
    if (!dragging.current) return;
    const s = st.current;
    if (s.natW * s.scale <= s.ww && s.natH * s.scale <= s.wh) return;
    mouseMoved.current = true;
    s.tx += e.clientX - gesture.current.px;
    s.ty += e.clientY - gesture.current.py;
    gesture.current.px = e.clientX;
    gesture.current.py = e.clientY;
    clamp();
    apply();
  };
  const mouseMoved = useRef(false);
  const lastTouchAt = useRef(0);
  const onMouseUp = () => {
    dragging.current = false;
  };
  const onClick = (e: React.MouseEvent) => {
    // Touch already handled the tap; the browser's compatibility click that
    // follows a touch must not schedule a second one (a double-tap would
    // otherwise zoom AND open the viewer).
    if (Date.now() - lastTouchAt.current < 800) return;
    if (mouseMoved.current) { mouseMoved.current = false; return; }
    if (!onTap) return;
    clearTimeout(tapTimer.current);
    const point = imagePoint(e.clientX, e.clientY);
    tapTimer.current = window.setTimeout(() => onTap(point), 280);
  };
  const onWheel = (e: React.WheelEvent) => {
    const s = st.current;
    const { x, y } = rectPoint(e.clientX, e.clientY);
    const ns = Math.min(
      s.min * 6,
      Math.max(s.min, s.scale * (e.deltaY < 0 ? 1.12 : 1 / 1.12)),
    );
    const f = ns / s.scale;
    s.tx = x - (x - s.tx) * f;
    s.ty = y - (y - s.ty) * f;
    s.scale = ns;
    clamp();
    apply();
  };

  return (
    <div
      className={'tma-poster-stage' + (className ? ' ' + className : '')}
      ref={wrapRef}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onTouchCancel={onTouchEnd}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseUp}
      onClick={onClick}
      onDoubleClick={(e) => { clearTimeout(tapTimer.current); doubleTap(e.clientX, e.clientY); }}
      onWheel={onWheel}
    >
      {/* eslint-disable-next-line jsx-a11y/img-redundant-alt */}
      <img
        ref={imgRef}
        className="tma-poster-img"
        src={src}
        alt="Architecture poster"
        draggable={false}
        onLoad={() => { fit(); if (targetPoint) focusTarget(targetPoint); }}
      />
      {targetPoint ? <span className="tma-poster-target-pin" ref={markerRef} aria-hidden="true" /> : null}
    </div>
  );
});
